// material-ui
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Modal,
    Checkbox,
    Grid,
    ToggleButtonGroup,
    ToggleButton,
    Button,
    Typography,
    TextField,
    Select,
    MenuItem
} from '@mui/material';
import { ManageAccounts, Cancel } from '@mui/icons-material';
import { styled, useTheme } from '@mui/material/styles';
import { useState, useEffect  } from 'react';
import TasklistFilter from './TasklistFilter';
import { deepPurple, grey } from '@mui/material/colors';
import useFetchData from 'hooks/useFetchData';
import { axiosInstance } from 'utils/axios';
import { useSelector } from 'react-redux';
import { selectCurrentToken } from 'features/auth/authSlice';
import { successAlert, errorAlert, warningAlert } from 'ui-component/Toasts';
import { useTranslation } from "react-i18next";

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
    '& .MuiToggleButtonGroup-grouped': {
        //   margin: theme.spacing(2),

        //   '&.Mui-disabled': {
        //     border: 0,
        //   },
        '&:not(:first-of-type)': {
            borderRadius: theme.shape.borderRadius
        },
        '&:first-of-type': {
            borderRadius: theme.shape.borderRadius
        }
    }
}));

const StyledToggleButton = styled(ToggleButton)({
    border: 2,
    borderColor: deepPurple[500],
    backgroundColor: grey[300],
    '&.Mui-selected, &.Mui-selected:hover': {
        color: 'white',
        backgroundColor: deepPurple[500]
    }
});

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: 1000,
    width: '90%',
    bgcolor: 'background.paper',
    boxShadow: 24,
    padding: '20px',
    borderRadius: '10px'
};

const CustomerNotificationsModal = ({
    customerNotificationModalOpen,
    setCustomerNotificationModalOpen,
    activeTab,
    buttonName,
    customerRow,
    handleRefresh
}) => {
    const { t } = useTranslation();
    const token = useSelector(selectCurrentToken);

    const [view, setView] = useState('resetTasklist');
    const [customerID, setCustomerID] = useState(0);
    
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(100);

    const handleChange = (event, nextView) => {
        setView(nextView);
    };

    const handleOpenModal = () => {
        setView(activeTab);
        setCustomerNotificationModalOpen(true);
        setCustomerID(customerRow.id);
    };

    useEffect(() => {
        if (customerNotificationModalOpen && customerRow?.id) {
            setCustomerID(customerRow.id);
            setView(activeTab); // or set to 'resetTasklist' by default
        }
    }, [customerNotificationModalOpen, customerRow]);

    function InsertNotificationTab({customerID}) {
        const [resetButtonDisabled, setResetButtonDisabled] = useState(false);
        const [title, setTitle] = useState('');
        const [content, setContent] = useState('');

        const submitResetCustomerTasklist = async () => {
            setResetButtonDisabled(true);

            try {
                const res = await axiosInstance.post(
                    '/CustomerNotification',
                    {
                        customerID: customerID,
                        title: title,
                        content: content
                    },
                    {
                        headers: {
                            Authorization: `Bearer ${token}`
                        }
                    }
                );
                if (res.status === 200) {
                    successAlert('成功创建通知');
                    handleRefresh();
                    setResetTasklistModalOpen(false);
                } else {
                    errorAlert('发生错误');
                }
            } catch (error) {
                console.log(error);
            } finally {
                setResetButtonDisabled(false);
            }
        };

        return (
            <Box style={{height: '80vh'}}>
                <Box>
                    <Grid item container pr={{ md: '5px' }}>
                        <Grid key='title' xs={5} sx={{ mt: 2 }} style={{alignContent: 'center'}}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                {t("customer.title")} : 
                            </Box>
                        </Grid>
                        <Grid key='title' xs={7} sx={{ mt: 2 }}>
                            <TextField
                                required
                                name="title"
                                type="text"
                                fullWidth
                                variant="outlined"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                            />
                        </Grid>
                    </Grid>
                    <Grid item container pr={{ md: '5px' }}>
                        <Grid key='content' xs={5} sx={{ mt: 2 }} style={{alignContent: 'center'}}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                {t("customer.content")} : 
                            </Box>
                        </Grid>
                        <Grid key='content' xs={7} sx={{ mt: 2 }}>
                            <TextField
                                multiline
                                required
                                name="content"
                                type="text"
                                fullWidth
                                variant="outlined"
                                value={content}
                                onChange={(e) => setContent(e.target.value)}
                                minRows={4}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                    e.stopPropagation(); // Prevent bubbling
                                    }
                                }}
                            />
                        </Grid>
                    </Grid>
                    <Button
                        variant="contained"
                        sx={{
                            display: 'block',
                            mx: 'auto',
                            width: '150px',
                            backgroundColor: deepPurple[500],
                            marginTop: '30px'
                        }}
                        onClick={submitResetCustomerTasklist}
                        disabled={resetButtonDisabled}
                    >
                        {t("customer.confirm")}
                    </Button>
                </Box>
            </Box>
        );
    }

    function NotificationRecordsTab({customerID}) {
        useEffect(() => {
            fetchResetCustomerTasklistRecords();
        },[]);

        const [resetCustomerTasklistRecords, setResetCustomerTasklistRecords] = useState([]);
        const fetchResetCustomerTasklistRecords = async () => {
            try {
                const res = await axiosInstance.get(`/CustomerNotification?customerID=${customerID}`, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
                if (res.status === 200) {
                    setResetCustomerTasklistRecords(res.data);
                }
            } catch (error) {

            }
        };

        return (
            <Box>
                <TableContainer sx={{ maxHeight: 440 }}>
                    <Table stickyHeader aria-label="sticky table">
                        <TableHead>
                            <TableRow>
                                <TableCell key='title' style={{ minWidth: 170 }}>{t("customer.title")}</TableCell>
                                <TableCell key='content' style={{ minWidth: 170 }}>{t("customer.content")}</TableCell>
                                <TableCell key='details' style={{ minWidth: 170 }}>{t("customer.details")}</TableCell>
                                <TableCell key='isOpened' style={{ minWidth: 50 }}>{t("customer.isOpened")}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {resetCustomerTasklistRecords.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => {
                                return (
                                    <TableRow hover role="checkbox" tabIndex={-1} key={row.code}>
                                        <TableCell key='title'>
                                            {row.title}
                                        </TableCell>
                                        <TableCell key='content' sx={{ whiteSpace: 'pre-line' }}>
                                            {row.content}
                                        </TableCell>
                                        <TableCell key='details'>
                                            {t("customer.by")}: {row.createdBy} {t("customer.created")}
                                            <br/>
                                            {
                                                `${row.createdDate.split('T')[0]} ${new Date(
                                                    '1970-01-01T' + row.createdDate.split('T')[1] + 'Z'
                                                ).toLocaleTimeString('en-US', {
                                                    timeZone: 'UTC',
                                                    hour12: true,
                                                    hour: 'numeric',
                                                    minute: 'numeric',
                                                    second: 'numeric'
                                                })}`
                                            }
                                        </TableCell>
                                        <TableCell key='isOpened'>
                                            {row.isOpened ? <>&#9989;</> : <>&#10060;</>}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        );
    }

    return (
        <>
            {/* <Button
                onClick={handleOpenModal}
                sx={{
                    border: 'none',
                    backgroundColor: deepPurple[500],
                    width: 'fit-content',
                    height: 'fit-content',
                    color: 'white',
                    '&.Mui-selected, &.Mui-selected:hover': {
                        color: deepPurple[500],
                        backgroundColor: 'white'
                    },
                    fontSize: '12px'
                }}
            >
                {buttonName}
            </Button> */}
            <Modal
                open={customerNotificationModalOpen}
                onClose={() => setCustomerNotificationModalOpen(false)}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <Box sx={style}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                        }}
                    >
                        <Typography id="modal-modal-title" variant="h3" component="h2">
                            {t("customer.customerNotification")}
                        </Typography>
                        <Cancel onClick={() => setCustomerNotificationModalOpen(false)} sx={{ width: '30px', height: '30px' }} />
                    </Box>
                    <Box
                        id="modal-modal-description"
                        sx={{
                            overflowY: 'auto'
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                width: '100%' 
                            }}
                        >
                            <StyledToggleButtonGroup
                                value={view}
                                exclusive
                                onChange={handleChange}
                                sx={{
                                    flexDirection: { xs: 'column', md: 'row' }
                                }}
                            >
                                <StyledToggleButton value="insertNotification" aria-label="insertNotification">
                                    {t("customer.insertNotification")}
                                </StyledToggleButton>
                                <StyledToggleButton value="notificationRecords" aria-label="notificationRecords">
                                    {t("customer.notificationRecords")}
                                </StyledToggleButton>
                            </StyledToggleButtonGroup>
                        </Box>

                        <Box>
                            {view === 'insertNotification' && (
                                <>
                                    <InsertNotificationTab customerID={customerID} />
                                </>
                            )}
                            {view === 'notificationRecords' && (
                                <>
                                    <NotificationRecordsTab customerID={customerID} />
                                </>
                            )}
                        </Box>
                    </Box>
                </Box>
            </Modal>
        </>
    );
};

export default CustomerNotificationsModal;